/**
 * Soft browser-notification opt-in card.
 *
 * The native permission dialog is never fired automatically — this card
 * explains the value first and only asks the browser after an explicit tap.
 * Guarded by `canShowSoftPrompt()`, so it never appears in an iframe/preview/
 * localhost, and never re-asks after a decision. A "Not now" dismissal only
 * silences the card for the current visit — it re-asks on the next visit
 * until the visitor subscribes.
 */
import { useEffect, useState } from "react";
import { Bell, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { canShowSoftPrompt, enablePush, rememberSoftPromptDismissed } from "@/lib/push/client";
import { trackPushPrompt } from "@/lib/push/telemetry";
import { useConsentRecorder } from "@/components/consent/ConsentBlock";

/** Give the page a beat to paint before the card slides in. */
const DELAY_MS = 2500;
const SOURCE = "auto_landing";

export function AutoPushOptIn() {
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const recordConsent = useConsentRecorder(SOURCE);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const t = window.setTimeout(() => setVisible(canShowSoftPrompt()), DELAY_MS);
    return () => window.clearTimeout(t);
  }, []);

  if (!visible) return null;

  const onEnable = async () => {
    setBusy(true);
    trackPushPrompt("prompted", { source: SOURCE });
    let granted = false;
    const result = await enablePush(SOURCE, {
      onPermissionGranted: () => {
        granted = true;
        trackPushPrompt("granted", { source: SOURCE });
      },
      onFailure: (reason) =>
        trackPushPrompt(
          reason === "blocked_context" || reason === "unsupported_browser" ? "unsupported" : "error",
          { source: SOURCE, reason },
        ),
    });
    setBusy(false);
    setVisible(false);

    if (result === "subscribed") {
      void recordConsent({ push: true }, { explicit: true });
      if (!granted) trackPushPrompt("granted", { source: SOURCE });
      toast.success("Notifications on for this device.");
    } else if (result === "denied") {
      // Browser-level refusal — recorded, but never a withdrawal.
      void recordConsent({ push: false }, { explicit: false });
      trackPushPrompt("denied", { source: SOURCE });
      toast("No problem, you can enable it later in browser settings.");
    } else if (result === "error") {
      toast.error("Could not turn on notifications. Please try again.");
    }
  };

  const onDismiss = () => {
    trackPushPrompt("dismissed", { source: SOURCE });
    // Dismissal is not a withdrawal.
    void recordConsent({ push: false }, { explicit: false });
    rememberSoftPromptDismissed();
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-label="Turn on browser updates"
      className="fixed inset-x-3 bottom-3 z-[70] sm:inset-x-auto sm:left-6 sm:bottom-6 sm:max-w-sm"
    >
      <div className="relative flex items-start gap-3 rounded-2xl border border-border bg-card/95 p-4 shadow-lg backdrop-blur">
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--brand)/0.1)] text-[hsl(var(--brand))]">
          <Bell className="h-4 w-4" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1 pr-4">
          <p className="text-sm font-semibold text-foreground">Never miss a deal.</p>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
            Get browser alerts for order updates, restocks, routine reminders and selected TruWe
            offers. You can switch them off anytime.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" onClick={onEnable} disabled={busy}>
              {busy ? "Turning on…" : "Enable notifications"}
            </Button>
            <Button size="sm" variant="ghost" onClick={onDismiss} disabled={busy}>
              Not now
            </Button>
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">
            Your browser will ask for permission next.
          </p>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss notification prompt"
          className="absolute right-2 top-2 rounded-full p-1 text-muted-foreground transition hover:bg-muted"
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
